import { Language } from "../model/Language.js";
import { DateUtil } from "../model/DateUtil.js";
import { BaseElement, BaseElementOptions } from "./BaseElement.js";

export interface DatepickerOptions extends BaseElementOptions {
    date: Date;
    minDate: Date;
    maxDate: Date;
    layout?: DatepickerLayout;
}

export type DatepickerLayout = "horizontal" | "vertical";

export class Datepicker extends BaseElement {
    public readonly element: HTMLDivElement;
    public onChange = e5.Signal.create<[Date]>();
    public onChangeEnd = e5.Signal.create<[Date]>();
    private _onClickItem = e5.Signal.create<[DatepickerItem]>();

    private _content: HTMLDivElement;
    private _actor: e5.Actor;
    private _offset: number = 0;
    private _items: DatepickerItem[] = [];
    private _vertical: boolean = false;
    private _speed: number = 1;
    private _current: DatepickerItem = null;
    private _currentDate: Date = new Date();
    private _navStart: HTMLButtonElement;
    private _navEnd: HTMLButtonElement;
    private _layout: DatepickerLayout;
    private _reverse: boolean = false;
    private _options: DatepickerOptions;
    private _triggerChange: boolean = true;

    public create(options: DatepickerOptions): this {
        super.create(options);

        this.setElement(this.createElement());

        this._layout = this.element.offsetWidth < this.element.offsetHeight ? "vertical" : "horizontal";//"vertical";
        this.element.className = "awi-datepicker awi-datepicker--" + this._layout;
        this.element.innerHTML = `
        <div class="awi-datepicker__shadow"></div>
        <div class="awi-datepicker__container">
            <div class="awi-datepicker__content"></div>
            <div class="awi-datepicker__fade awi-datepicker__fade--start"></div>
            <div class="awi-datepicker__fade awi-datepicker__fade--end"></div>
        </div>
        <button class="awi-datepicker__nav awi-datepicker__nav--start fal fa-arrow-circle-left"></button>
        <button class="awi-datepicker__nav awi-datepicker__nav--end fal fa-arrow-circle-right"></button>
        `;

        this._content = this.element.querySelector(".awi-datepicker__content");
        var container = this.element.querySelector(".awi-datepicker__container") as HTMLElement;
        this._navStart = this.element.querySelector(".awi-datepicker__nav--start");
        this._navEnd = this.element.querySelector(".awi-datepicker__nav--end");

        options.container.appendChild(this.element);

        this._reverse = this._layout === "vertical";
        this._vertical = this._layout === "vertical";
        this._options = options;

        this._actor = new e5.Actor(container, {
            stopPropagation: true,
            preventDefault: true,
            maxPointers: 1
        });

        this._actor.onDown.add((pointers) => {
            gsap.killTweensOf(this);
            this._triggerChange = true;
            // this._startDragDate = this.date;
        });

        this._actor.onMove.add((pointers) => {
            var pointer = pointers[0];
            if (this._vertical)
                this.offset += pointer.deltaY;
            else
                this.offset += pointer.deltaX;
        });

        this._actor.onUp.add((pointers) => {
            var pointer = pointers[0];
            if (Math.abs(this._speed) > 7) {
                var neg = this._speed < 0 ? -1 : 1;
                this.dragEnd(this.offset + Math.pow(this._speed * 2, 2) * neg);
            } else {
                this.dragEnd(this.offset, 0.25);
            }
        });

        var countToDays = (count: number): number => {
            var daysPerClick = 1;
            if (count > 3)
                daysPerClick = 2;
            if (count > 6)
                daysPerClick = 4;
            return daysPerClick;
        }

        var leftClick = new e5.Click(this._navStart);
        leftClick.onRepeat.add((count: number) => {
            var delta = countToDays(count) * this.itemSize;
            // if (!this._reverse) {
            //     delta *= -1;
            // }
            this.tweenTo(this.offset + delta);
        });

        var rightClick = new e5.Click(this._navEnd);
        rightClick.onRepeat.add((count: number) => {
            var delta = countToDays(count) * this.itemSize;
            // if (this._reverse) {
            //     delta *= -1;
            // }
            this.tweenTo(this.offset - delta);
        });

        this.element.addEventListener("wheel", (e) => {
            if (!this._vertical)
                return;

            var daysPerWheelDelta = 1;
            if (e.deltaY < 0) {
                this.tweenTo(this.offsetTarget + daysPerWheelDelta * this.itemSize);
            } else {
                this.tweenTo(this.offsetTarget - daysPerWheelDelta * this.itemSize);
            }
        }, e5.Caps.passiveEventLister ? {
            passive: true
        } : false);

        this._onClickItem.add((item) => {
            // var d = item.date;
            // setTimeout(() => {
            //     var offset = this.dateToOffset(d);
            //     this.tweenTo(offset);
            // }, 50);

            setTimeout(() => {
                this.setDate(item.date);
            }, 50);
        });

        this.update();
        this.setDate(options.date, 0);//options.minDate;

        this.resize();

        // window.addEventListener("resize", () => {
        //     this.resize();
        //     // this.layout = options.container.offsetWidth < options.container.offsetHeight ? "vertical" : "horizontal";
        // });

        this.updateNavLabels();

        return this;
    }

    private updateNavLabels(): void {

        var start = this._reverse ? this._navEnd : this._navStart;
        var end = this._reverse ? this._navStart : this._navEnd;

        lang.set(start, {
            type: "@title",
            de: "Früheres Datum",
            en: "Earlier date"
        });

        lang.set(end, {
            type: "@title",
            de: "Späteres Datum",
            en: "Later date"
        });
    }

    protected createElement(): HTMLDivElement {
        var e = document.createElement("div");
        e.className = "awi-datepicker";
        return e;
    }

    public get layout(): DatepickerLayout {
        return this._layout;
    }

    public set layout(layout: DatepickerLayout) {
        if (this._layout === layout)
            return;
        var before = this._layout;
        this._layout = layout;
        this._reverse = layout === "vertical";
        this._vertical = layout === "vertical";
        this.element.classList.replace("awi-datepicker--" + before, "awi-datepicker--" + layout);
        this.updateNavState();
        this.updateNavLabels();
        // this.element.setAttribute("data-layout", layout);
        // this.resize();
    }

    private tweenTo(offset: number, time: number = 0.5, triggerChange: boolean = true): void {
        gsap.killTweensOf(this);
        this._triggerChange = true;
        this.dragEnd(offset, time, triggerChange);
    }

    private _offsetTarget: number = NaN;
    private get offsetTarget(): number {
        return isNaN(this._offsetTarget) ? this.offset : this._offsetTarget;
    }

    private dragEnd(offset: number, time: number = 0.5, triggerChange: boolean = true): void {
        offset = this.locked(offset);

        this._triggerChange = triggerChange;
        this._offsetTarget = offset;
        gsap.to(this, {
            duration: time,
            onOverwrite: () => {
                this._triggerChange = true;
                this._offsetTarget = NaN;
            },
            offset: offset,
            onComplete: () => {
                this._triggerChange = true;
                this._offsetTarget = NaN;
                if (triggerChange) {
                    this.onChangeEnd.dispatch(this.date);
                }
            }
        });
    }

    private locked(offset: number): number {
        var o = (this.containerSize * 0.5) - (this.itemSize * 0.5);
        offset = e5.Calc.roundTo(offset - o, this.itemSize) + o;
        return this.clamp(offset);
    }

    public resize(): void {
        this.layout = this.element.offsetWidth < this.element.offsetHeight ? "vertical" : "horizontal";
        this.offset = this.locked(this.dateToOffset(this._current.date));
        this._speed = 1;
    }

    public get date(): Date {
        return this._current ? new Date(this._current.date) : new Date(this.minDate);
    }

    public setDate(date: Date, speed: number | "auto" = "auto", triggerChange: boolean = true) {
        date = DateUtil.clamp(date, this._options.minDate, this._options.maxDate);
        if (DateUtil.equal(date, this.date)) {
            return;
        }

        var duration = 1;
        if (typeof speed === "number") {
            if (speed <= 0) {
                this._triggerChange = triggerChange;
                this.offset = this.locked(this.dateToOffset(date));
                this._speed = speed;
                this._triggerChange = true;
                return;
            }
            duration = speed;
        } else {
            duration = 1;
        }

        var offset = this.dateToOffset(date);
        this.tweenTo(offset, duration, triggerChange);
    }

    private dateToOffset(date: Date): number {

        if (this._reverse) {
            const diffDays = DateUtil.dayDiff(this.maxDate, date);
            return -(diffDays * this.itemSize) + (this.containerSize * 0.5 - this.itemSize * 0.5);
        }
        else {
            const diffDays = DateUtil.dayDiff(date, this.minDate);
            return -(diffDays * this.itemSize) + (this.containerSize * 0.5 - this.itemSize * 0.5);
        }
    }

    private clamp(offset: number): number {
        return e5.Calc.clamp(offset, this.minOffset, this.maxOffset);
    }

    private get minOffset(): number {
        return this._reverse ? this.dateToOffset(this.minDate) : this.dateToOffset(this.maxDate);
    }

    private get maxOffset(): number {
        return this._reverse ? this.dateToOffset(this.maxDate) : this.dateToOffset(this.minDate);
    }

    public get minDate(): Date {
        return this._options.minDate;
    }

    public get maxDate(): Date {
        return this._options.maxDate;
    }

    private get offset(): number {
        return this._offset;
    }

    private set offset(offset: number) {
        offset = this.clamp(offset);
        if (this._offset === offset)
            return;
        this._speed = offset - this._offset;
        this._offset = offset;

        // this._navEnd.classList.toggle("awi-datepicker__nav--active", this._offset !== this.minOffset);
        // this._navStart.classList.toggle("awi-datepicker__nav--active", this._offset !== this.maxOffset);

        if (this._vertical) {
            // this._content.style.transform = "translateY(" + this._offset + "px)";
            this._content.style.transform = `translate3d(0, ${this._offset}px, 0)`;
        } else {
            // this._content.style.transform = "translateX(" + this._offset + "px)";
            this._content.style.transform = `translate3d(${this._offset}px, 0, 0)`;
        }

        this.update();
    }

    private update(): void {
        var count = Math.ceil(this.containerSize / this.itemSize) + 1;
        var len = e5.Calc.clamp(count - this._items.length, 0, 50);
        for (var i = 0; i < len; ++i)
            this.addItem();

        var centerLeft = (this.containerSize * 0.5) - this.offset;

        var current: DatepickerItem = null;
        var start = Math.floor(-this.offset / this.itemSize) * this.itemSize;
        for (var i = 0; i < this._items.length; ++i) {
            var item = this._items[i];
            var off = Math.abs(centerLeft - start)
            if (!current && off < this.itemSize) {
                current = item;
            }
            this.updateItem(item, start);
            start += this.itemSize;
        }
        this.current = current;
    }

    private updateItem(item: DatepickerItem, offset: number): void {
        var dayOffset = Math.floor(offset / this.itemSize);

        if (this._reverse) {
            var d = new Date(this.maxDate);
            d.setUTCDate(d.getUTCDate() - dayOffset);
        } else {
            var d = new Date(this.minDate);
            d.setUTCDate(d.getUTCDate() + dayOffset);
        }

        var ratio = ((offset + this._offset) + this.itemSize * 0.5) / this.containerSize;
        item.update(d, offset, e5.Calc.clamp(ratio, 0, 1));

        if (this._vertical) {
            // item.element.style.transform = "translateY(" + offset + "px)";
            item.element.style.transform = `translate3d(0, ${offset}px, 0)`;
        } else {
            // item.element.style.transform = "translateX(" + offset + "px)";
            item.element.style.transform = `translate3d(${offset}px, 0, 0)`;
        }
    }

    private get current(): DatepickerItem {
        return this._current;
    }

    private set current(item: DatepickerItem) {
        if (!item) {
            console.warn("NO CURRENT SET!!!!!");
            return;
        }

        if (this._current) {
            this._current.active = false;
        }
        this._current = item;
        this._current.active = true;

        this.updateNavState();

        if (!this._currentDate || this._current.date.getTime() != this._currentDate.getTime()) {
            if (this._triggerChange) {
                if (this._currentDate
                    && DateUtil.equal(this._currentDate, this._current.date)) {
                } else {
                    this.onChange.dispatch(new Date(this._current.date));
                }
            }
            this._currentDate = new Date(this._current.date);
        }
    }

    private updateNavState(): void {
        var startActive = this._current ? DateUtil.smaller(this._current.date, this.maxDate) : false;
        var endActive = this._current ? DateUtil.greater(this._current.date, this.minDate) : false;


        this._navStart.classList.toggle("awi-datepicker__nav--active", this._reverse ? startActive : endActive);
        this._navStart.tabIndex = this._navStart.classList.contains("awi-datepicker__nav--active") ? 0 : -1;

        this._navEnd.classList.toggle("awi-datepicker__nav--active", this._reverse ? endActive : startActive);
        this._navEnd.tabIndex = this._navEnd.classList.contains("awi-datepicker__nav--active") ? 0 : -1;
    }

    private get itemSize(): number {
        return this._vertical ? 100 : 200;
    }

    private get containerSize(): number {
        return this._vertical ? this.element.offsetHeight : this.element.offsetWidth;
    }

    private addItem(): void {
        var item = new DatepickerItem(this, this._onClickItem);
        this._items.push(item);
        this._content.appendChild(item.element);
    }
}

class DatepickerItem {
    public readonly element: HTMLButtonElement;

    public offset: number = 0;
    public date: Date = new Date();

    private _dayName: HTMLDivElement;
    private _day: HTMLDivElement;
    private _month: HTMLDivElement;
    private _year: HTMLDivElement;
    private _content: HTMLDivElement;

    constructor(private _datePicker: Datepicker, private _onClick: e5.Signal<[DatepickerItem]>) {
        this.element = document.createElement("button");
        this.element.className = "awi-datepicker__item";
        this.element.tabIndex = -1;
        this.element.innerHTML = `
            <div class="awi-datepicker__item-content">
                <div class="awi-datepicker__item-date awi-datepicker__item-date--day-name"></div>
                <div class="awi-datepicker__item-date awi-datepicker__item-date--day"></div>
                <div class="awi-datepicker__item-date awi-datepicker__item-date--month"></div>
                <div class="awi-datepicker__item-date awi-datepicker__item-date--year"></div>
            </div>
        `;
        this._content = this.element.querySelector(".awi-datepicker__item-content");
        this._dayName = this.element.querySelector(".awi-datepicker__item-date--day-name");
        this._day = this.element.querySelector(".awi-datepicker__item-date--day");
        this._month = this.element.querySelector(".awi-datepicker__item-date--month");
        this._year = this.element.querySelector(".awi-datepicker__item-date--year");

        var click = new e5.Click(this.element);
        click.onClick.add(() => this._onClick.dispatch(this));
        this.element.addEventListener("keyup", (e) => {
            if (e.keyCode === e5.KeyCode.SPACE
                || e.keyCode === e5.KeyCode.ENTER) {
                this._onClick.dispatch(this);
            }
        });
    }

    public update(date: Date, offset: number, screenRatio: number): void {

        this.element.disabled = DateUtil.smaller(date, this._datePicker.minDate) || DateUtil.greater(date, this._datePicker.maxDate);
        this.element.classList.toggle("awi-datepicker__item--disabled", this.element.disabled);
        this.offset = offset;
        this.date = date;
        lang.set(this._dayName, {
            de: e5.Text.day(date.getUTCDay(), "de"),
            en: e5.Text.day(date.getUTCDay(), "en")
        });
        this._day.textContent = (date.getUTCDate()) + ".";
        lang.set(this._month, {
            de: e5.Text.month(date.getUTCMonth(), "de"),
            en: e5.Text.month(date.getUTCMonth(), "en")
        });
        this._year.textContent = date.getUTCFullYear() + "";


        screenRatio = 1 - Math.abs((screenRatio - 0.5) * 2);
        // screenRatio *= screenRatio;
        if (this._datePicker.layout === "horizontal") {
            screenRatio *= 1;
        } else {
            screenRatio *= 1.75;
        }
        screenRatio = e5.Calc.clamp(screenRatio, 0, 1);
        // var opacity = 1;
        // var ratio = (offset + this._datePicker.itemSize * 0.5) / this._datePicker.containerSize;
        // opacity = ratio;

        this.element.style.opacity = screenRatio + "";//opacity + "";
    }

    public get active(): boolean {
        return this.element.classList.contains("awi-datepicker__item--active");
    }

    public set active(active: boolean) {
        this.element.classList.toggle("awi-datepicker__item--active", active);
    }
}